import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Alert, Button, Typography, Stack } from "@mui/joy";
import RefreshIcon from "@mui/icons-material/Refresh";

interface Props {
  children: ReactNode;
  onReport?: (error: Error, componentStack: string) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("App error boundary caught:", error, errorInfo);
    this.props.onReport?.(error, errorInfo.componentStack || "");
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <Stack spacing={2} sx={{ maxWidth: 480, textAlign: "center" }}>
            <Alert color="danger" variant="soft">
              Something went wrong.
            </Alert>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              {this.state.error?.message || "An unexpected error occurred."}
            </Typography>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<RefreshIcon />}
              onClick={this.handleReload}
            >
              Reload
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
